import * as Sentry from "@sentry/react";
import axios from "axios";
import type { Story } from "@theirstory/common/api_types/backend";

import type { ActionTypeHelper } from "../state/rootStore";
import type {
  PortalAppearance,
  PortalContentItem,
  PortalDefaultLayout,
  PortalSettings,
  PortalThumbnailFit,
  ResearchPortalDetail,
} from "../reducers/portalsReducer";

import {
  PORTALS_LOADING,
  PORTALS_LOADED,
  PORTAL_AUTOSAVE_STATUS_SET,
  PORTAL_CONTENT_LOADED,
  PORTAL_CONTENT_REPROCESSES_LOADED,
  PORTAL_CONTENT_TITLE_UPDATED,
  PORTAL_CONTENT_VISIBILITY_UPDATED,
  PORTAL_DELETED,
  PORTAL_DETAILS_LOADING,
  PORTAL_DETAILS_LOADED,
  PORTAL_PUBLISH_PROGRESS_LOADED,
  PORTAL_THUMBNAIL_UPDATED,
  PORTAL_UPDATED,
} from "./types";

interface CreatePortalPayload {
  title: string;
  description: string;
  slug: string;
  linkedProjectIds: string[];
}

interface CreatePortalCollectionPayload {
  bannerURL?: string;
  description: string;
  title: string;
}

interface UpdatePortalCollectionPayload extends CreatePortalCollectionPayload {
  collectionId: string;
}

type UpdatePortalAppearancePayload = Omit<Partial<PortalAppearance>, "defaultLayout" | "primary" | "secondary"> & {
  defaultLayout?: PortalDefaultLayout;
  primary?: string;
  secondary?: string;
};

type PortalPublishProgressPayload = Pick<
  ResearchPortalDetail,
  "_id" | "draftDiff" | "infrastructureStatus" | "liveVersion" | "pendingLiveVersion" | "publishProgress" | "status"
>;

type PortalContentReprocessPayload = Pick<ResearchPortalDetail, "_id" | "contentReprocesses" | "draftDiff">;

export interface UpdatePortalPayload {
  appearance?: UpdatePortalAppearancePayload;
  description?: string;
  settings?: Partial<PortalSettings>;
  title?: string;
}

const runPortalRequest = async <T>(
  method: string,
  request: () => Promise<T>,
  tags: Record<string, string> = {}
): Promise<T> => {
  try {
    return await request();
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "portals");
      scope.setTag("method", method);
      Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
      Sentry.captureException(error);
    });
    throw error;
  }
};

export const storyToPortalContentItem = (story: Story): PortalContentItem => ({
  _id: story._id,
  type: "story",
  title: story.title || "Untitled interview",
  creator: story.creator?.full_name || story.participants?.[0]?.name || "Unknown creator",
  duration: story.duration || 0,
  projectName: story.project?.name || "No project",
  mediaType: story.custom_archive_media_type,
  thumbnailURL: story.getThumbnailURL || story.thumbnailURL || "",
  hidden: false,
});

export const setPortalAutosaving = (isAutosaving: boolean) => ({
  type: PORTAL_AUTOSAVE_STATUS_SET,
  payload: isAutosaving,
});

export const createPortal =
  (payload: CreatePortalPayload): ActionTypeHelper<Promise<ResearchPortalDetail>> =>
  async (dispatch) => {
    const { data: portal } = await runPortalRequest("createPortal", () =>
      axios.post<ResearchPortalDetail>("/portals/create", payload)
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const checkPortalSlugAvailability =
  (slug: string): ActionTypeHelper<Promise<boolean>> =>
  async () => {
    const { data } = await runPortalRequest("checkPortalSlugAvailability", () =>
      axios.get<{ available: boolean }>("/portals/slug-availability", {
        params: { slug },
      })
    );
    return data.available;
  };

export const loadPortals =
  (options: { force?: boolean } = {}): ActionTypeHelper<Promise<ResearchPortalDetail[]>> =>
  async (dispatch, getState) => {
    const { isLoadingPortals, portals } = getState().portals;

    if (isLoadingPortals || (!options.force && portals.length > 0)) {
      return portals;
    }

    dispatch({ type: PORTALS_LOADING });
    const { data: loadedPortals } = await runPortalRequest("loadPortals", () =>
      axios.get<ResearchPortalDetail[]>("/portals")
    );
    dispatch({ type: PORTALS_LOADED, payload: loadedPortals });
    return loadedPortals;
  };

export const loadPortalDetails =
  (portalId: string): ActionTypeHelper<Promise<ResearchPortalDetail>> =>
  async (dispatch) => {
    dispatch({ type: PORTAL_DETAILS_LOADING });
    const { data: portal } = await runPortalRequest(
      "loadPortalDetails",
      () => axios.get<ResearchPortalDetail>(`/portals/${portalId}`),
      { portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const loadPortalPublishProgress =
  (portalId: string): ActionTypeHelper<Promise<PortalPublishProgressPayload>> =>
  async (dispatch) => {
    const { data: progress } = await runPortalRequest(
      "loadPortalPublishProgress",
      () => axios.get<PortalPublishProgressPayload>(`/portals/${portalId}/publish-progress`),
      { portalId }
    );
    dispatch({ type: PORTAL_PUBLISH_PROGRESS_LOADED, payload: progress });
    return progress;
  };

export const loadPortalContentReprocesses =
  (portalId: string): ActionTypeHelper<Promise<PortalContentReprocessPayload>> =>
  async (dispatch) => {
    const { data: reprocesses } = await runPortalRequest(
      "loadPortalContentReprocesses",
      () => axios.get<PortalContentReprocessPayload>(`/portals/${portalId}/content-reprocesses`),
      { portalId }
    );
    dispatch({ type: PORTAL_CONTENT_REPROCESSES_LOADED, payload: reprocesses });
    return reprocesses;
  };

export const updatePortal =
  (portalId: string, payload: UpdatePortalPayload): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch) => {
    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "updatePortal",
      () => axios.put<ResearchPortalDetail>(`/portals/${portalId}`, payload),
      { portalId }
    );
    dispatch({ type: PORTAL_UPDATED, payload: portal });
    return portal;
  };

export const createPortalCollection =
  (portalId: string, payload: CreatePortalCollectionPayload): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch) => {
    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "createPortalCollection",
      () => axios.post<ResearchPortalDetail>(`/portals/${portalId}/collections`, payload),
      { portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const updatePortalCollection =
  (portalId: string, payload: UpdatePortalCollectionPayload): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch) => {
    if (!portalId || !payload.collectionId) return undefined;

    const { data: portal } = await runPortalRequest(
      "updatePortalCollection",
      () =>
        axios.put<ResearchPortalDetail>(`/portals/${portalId}/collections/${payload.collectionId}`, {
          bannerURL: payload.bannerURL,
          description: payload.description,
          title: payload.title,
        }),
      { collectionId: payload.collectionId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const deletePortalCollection =
  (portalId: string, collectionId: string): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch) => {
    if (!portalId || !collectionId) return undefined;

    const { data: portal } = await runPortalRequest(
      "deletePortalCollection",
      () => axios.delete<ResearchPortalDetail>(`/portals/${portalId}/collections/${collectionId}`),
      { collectionId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const setPortalContent =
  (content: PortalContentItem[], collectionId: string): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;

    if (!portalId) return undefined;

    return dispatch(setPortalCollectionContent(portalId, collectionId, content));
  };

export const setPortalCollectionContent =
  (
    portalId: string,
    collectionId: string,
    content: PortalContentItem[]
  ): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch) => {
    if (!portalId) return undefined;

    dispatch({ type: PORTAL_CONTENT_LOADED, payload: { collectionId, content, portalId } });

    const { data: portal } = await runPortalRequest(
      "setPortalContent",
      () =>
        axios.put<ResearchPortalDetail>(`/portals/${portalId}/content`, {
          collectionId,
          content,
        }),
      { collectionId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const updatePortalContentTitle =
  (collectionId: string, itemId: string, title: string): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;
    dispatch({ type: PORTAL_CONTENT_TITLE_UPDATED, payload: { collectionId, itemId, title } });

    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "updatePortalContentTitle",
      () =>
        axios.put<ResearchPortalDetail>(`/portals/${portalId}/content/${itemId}`, {
          collectionId,
          title,
        }),
      { collectionId, itemId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const updatePortalContentThumbnail =
  (
    collectionId: string,
    itemId: string,
    thumbnailURL: string
  ): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;

    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "updatePortalContentThumbnail",
      () =>
        axios.put<ResearchPortalDetail>(`/portals/${portalId}/content/${itemId}`, {
          collectionId,
          thumbnailURL,
        }),
      { collectionId, itemId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const loadStoryFileImage =
  (storyId: string, fileId: string): ActionTypeHelper<Promise<string>> =>
  async () => {
    const { data } = await runPortalRequest(
      "loadStoryFileImage",
      () => axios.get<Blob>(`/stories/${storyId}/file/${fileId}/image`, { responseType: "blob" }),
      { fileId, storyId }
    );

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(data);
    });
  };

export const removePortalContentItem =
  (collectionId: string, itemId: string): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;

    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "removePortalContentItem",
      () => axios.delete<ResearchPortalDetail>(`/portals/${portalId}/collections/${collectionId}/content/${itemId}`),
      { collectionId, itemId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const restorePortalContentItem =
  (collectionId: string, itemId: string): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;

    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "restorePortalContentItem",
      () =>
        axios.post<ResearchPortalDetail>(`/portals/${portalId}/collections/${collectionId}/content/${itemId}/restore`),
      { collectionId, itemId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const reprocessPortalContentItem =
  (collectionId: string, itemId: string): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;

    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "reprocessPortalContentItem",
      () =>
        axios.post<ResearchPortalDetail>(
          `/portals/${portalId}/collections/${collectionId}/content/${itemId}/reprocess`
        ),
      { collectionId, itemId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const setPortalContentHidden =
  (collectionId: string, itemId: string, hidden: boolean): ActionTypeHelper<Promise<ResearchPortalDetail | void>> =>
  async (dispatch, getState) => {
    const portalId = getState().portals.portalDetail._id;
    dispatch({ type: PORTAL_CONTENT_VISIBILITY_UPDATED, payload: { collectionId, itemId, hidden } });

    if (!portalId) return undefined;

    const { data: portal } = await runPortalRequest(
      "setPortalContentHidden",
      () =>
        axios.put<ResearchPortalDetail>(`/portals/${portalId}/content/${itemId}`, {
          collectionId,
          hidden,
        }),
      { collectionId, itemId, portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const updatePortalThumbnail =
  (
    portalId: string,
    thumbnailURL: string,
    thumbnailFit: PortalThumbnailFit
  ): ActionTypeHelper<Promise<ResearchPortalDetail>> =>
  async (dispatch) => {
    const { data: portal } = await runPortalRequest(
      "updatePortalThumbnail",
      () =>
        axios.put<ResearchPortalDetail>(`/portals/${portalId}/thumbnail`, {
          thumbnailFit,
          thumbnailURL,
        }),
      { portalId }
    );
    dispatch({
      type: PORTAL_THUMBNAIL_UPDATED,
      payload: {
        draftDiff: portal.draftDiff,
        portalId,
        thumbnailFit: portal.thumbnailFit,
        thumbnailURL: portal.thumbnailURL,
      },
    });
    return portal;
  };

export const publishPortal =
  (portalId: string): ActionTypeHelper<Promise<ResearchPortalDetail>> =>
  async (dispatch) => {
    const { data: portal } = await runPortalRequest(
      "publishPortal",
      () => axios.put<ResearchPortalDetail>(`/portals/${portalId}/publish`),
      { portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const unpublishPortal =
  (portalId: string): ActionTypeHelper<Promise<ResearchPortalDetail>> =>
  async (dispatch) => {
    const { data: portal } = await runPortalRequest(
      "unpublishPortal",
      () => axios.put<ResearchPortalDetail>(`/portals/${portalId}/unpublish`),
      { portalId }
    );
    dispatch({ type: PORTAL_DETAILS_LOADED, payload: portal });
    return portal;
  };

export const deletePortal =
  (portalId: string): ActionTypeHelper<Promise<void>> =>
  async (dispatch) => {
    await runPortalRequest("deletePortal", () => axios.delete(`/portals/${portalId}`), { portalId });
    dispatch({ type: PORTAL_DELETED, payload: portalId });
  };
